/**
 * Helpers for mapping audit correction field paths onto the output schema.
 *
 * Audit corrections may target nested leaf paths (`procedure.0.coverage.copay`,
 * `drug[1].pbm.name`) while the auditor output panel renders one row per ROOT
 * schema field (`procedure`, `drug`, …). These helpers split a raw correction
 * path into its root field id + a bracket-form leaf path (`[0].coverage.copay`)
 * so leaf corrections can be counted and rendered under their root field row.
 */

import type { FieldGroup } from '@/types/formSchema';
import { resolveHandoffFieldTargets } from '@/utils/handoffField';

/** A non-KEEP correction on a nested leaf, grouped under its root field. */
export interface LeafCorrection {
  /** Leaf path relative to the root field, bracket form: "[0].coverage.copay". */
  path: string;
  /** Upper-cased action: "CHANGE" | "ADD" | "REMOVE". */
  action: string;
  original_value?: unknown;
  corrected_value?: unknown;
  reasoning?: string;
}

/** Structural view of an audit correction — kept dependency-free on purpose. */
export interface CorrectionLike {
  field?: string;
  action?: string;
  original_value?: unknown;
  corrected_value?: unknown;
  reasoning?: string;
}

/**
 * Split a raw correction path into its root field id and a bracket-form leaf
 * path. Accepts both dot-index ("procedure.0.coverage.copay") and bracket-index
 * ("drug[1].pbm.name") forms; a flat root-level path yields an empty leafPath.
 * Bracket form matters downstream: numeric dot segments would otherwise render
 * as bogus "0 ›" labels in the humanized leaf breadcrumb.
 */
export function splitCorrectionPath(field: string): { rootId: string; leafPath: string } {
  const segments = field
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(s => s !== '');
  if (segments.length === 0) return { rootId: '', leafPath: '' };
  const [rootId, ...rest] = segments;
  let leafPath = '';
  for (const seg of rest) {
    if (/^\d+$/.test(seg)) leafPath += `[${seg}]`;
    else leafPath += leafPath ? `.${seg}` : seg;
  }
  return { rootId, leafPath };
}

/**
 * Group non-KEEP corrections that target nested leaf paths by their root field
 * id, preserving audit order. Flat root-level corrections are skipped — they
 * already flow through the existing per-field correction lookup.
 */
export function groupLeafCorrections(
  corrections: CorrectionLike[],
  /**
   * Output schema. When given, a correction whose first segment is not a root field is
   * resolved against the schema instead of being dropped, so a nested leaf named by a
   * short or partial name still renders under the field it belongs to.
   */
  groups?: FieldGroup[],
): Record<string, LeafCorrection[]> {
  const map: Record<string, LeafCorrection[]> = {};
  for (const c of corrections) {
    const placed = placeLeafCorrection(c, groups);
    if (!placed) continue;
    const { rootId, leafPath, action } = placed;
    if (!map[rootId]) map[rootId] = [];
    map[rootId].push({
      path: leafPath,
      action,
      original_value: c.original_value,
      corrected_value: c.corrected_value,
      reasoning: c.reasoning,
    });
  }
  return map;
}

/**
 * Where one correction's leaf row belongs, or undefined when it has none: a KEEP, a
 * field-less record, a flat root-level path (already covered by the per-field lookup), or
 * — when a schema is supplied — a path the schema cannot place. That last rule is the same
 * one the changed-field count applies, so the pill and the rows can never disagree.
 */
function placeLeafCorrection(
  c: CorrectionLike,
  groups?: FieldGroup[],
): { rootId: string; leafPath: string; action: string } | undefined {
  if (!isChangedCorrection(c)) return undefined;
  const target = groups ? resolveCorrectionTarget(groups, c.field) : splitCorrectionPath(c.field!);
  if (!target?.rootId || !target.leafPath) return undefined;
  return { ...target, action: (c.action ?? '').toUpperCase() };
}

/**
 * Resolve a bracket-form leaf path against a root field value. Returns
 * undefined on any miss (missing key, out-of-range index, non-object
 * intermediate, empty path). Used as the "before" fallback for AI audits,
 * whose corrections carry no original_value.
 */
export function getValueAtPath(root: unknown, leafPath: string): unknown {
  if (!leafPath) return undefined;
  const regex = /([^.[\]]+)|\[(\d+)\]/g;
  let current: unknown = root;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(leafPath)) !== null) {
    if (current === null || typeof current !== 'object') return undefined;
    if (m[2] !== undefined) {
      if (!Array.isArray(current)) return undefined;
      current = current[Number(m[2])];
    } else {
      if (Array.isArray(current)) return undefined;
      current = (current as Record<string, unknown>)[m[1]];
    }
  }
  return current;
}

/** True for a correction that asserts a change (anything but KEEP / blank). */
export function isChangedCorrection(c: CorrectionLike): boolean {
  const action = (c.action ?? '').toUpperCase();
  return !!c.field && !!action && action !== 'KEEP';
}

/**
 * Where a correction lands in the schema: the ROOT field whose row can show it, plus the
 * leaf path under that root. Returns undefined when the current schema has nothing that
 * could render it.
 *
 * The auditor emits concrete paths (`drug.4.coverage.copay`), whose first segment IS the
 * root field — the fast path. But it may also name a nested leaf by a short or partial
 * name (`copay`, `coverage.copay`), which has no row of its own and whose first segment is
 * not a schema field. Those used to be dropped on the floor: the correction counted as no
 * changed field and rendered as no row, so an audit whose changes were all nested could
 * report "Changed fields 0". Falling back to the schema resolver — the same one the
 * handoff actions use — finds where such a leaf actually lives and rewrites the
 * correction onto that concrete path.
 */
export function resolveCorrectionTarget(
  groups: FieldGroup[],
  field: string | undefined,
): { rootId: string; leafPath: string } | undefined {
  if (!field) return undefined;
  const raw = splitCorrectionPath(field);
  if (!raw.rootId) return undefined;
  for (const group of groups) {
    for (const f of group.fields ?? []) {
      if (f.id === raw.rootId) return raw;
    }
  }
  // Not a root field — resolve the name against every schema leaf and take the concrete
  // path it lands on.
  const [target] = resolveHandoffFieldTargets(groups, undefined, field);
  if (!target) return undefined;
  const resolved = splitCorrectionPath(target.path);
  return resolved.rootId ? resolved : undefined;
}

export interface LeafDiff {
  path: string;
  before: unknown;
  after: unknown;
}

/** A list with no nested structure — a multi-select value such as `["PHONE", "FAX"]`. */
function isScalarList(value: unknown[]): boolean {
  return value.every(item => item === null || typeof item !== 'object');
}

/** A value `flattenLeaves` records as-is rather than descending into. */
function isLeafValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length === 0 || isScalarList(value);
  return value === null || typeof value !== 'object';
}

/**
 * Flatten a (possibly nested / arrayed) value into a map of leaf-path → scalar.
 * Arrays of entities use `[i]` segments, objects use dotted keys. Empty objects
 * contribute no leaves; scalars, empty arrays and lists of scalars (multi-select
 * values) are leaves themselves. Used to diff nested output fields (e.g. `drug`) at
 * the leaf level (BI-1246).
 *
 * A multi-select list is ONE field (LA-7951). Splitting it into `methods[0]`,
 * `methods[1]`… gave each option its own row and Revert, and reverting one option on
 * its own is meaningless — reverting `[1]` alone yields a list the case never held, and
 * reverting an option the audit dropped writes `null` into the list.
 */
function flattenLeaves(value: unknown, prefix: string, out: Record<string, unknown>): void {
  if (isLeafValue(value)) {
    if (prefix) out[prefix] = value ?? null;
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, i) => flattenLeaves(item, `${prefix}[${i}]`, out));
    return;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    flattenLeaves(v, prefix ? `${prefix}.${k}` : k, out);
  }
}

function leafValuesEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Changed leaves between a nested field's original and corrected values. */
export function computeLeafDiffs(original: unknown, corrected: unknown): LeafDiff[] {
  const a: Record<string, unknown> = {};
  const b: Record<string, unknown> = {};
  flattenLeaves(original, '', a);
  flattenLeaves(corrected, '', b);
  const paths = new Set([...Object.keys(a), ...Object.keys(b)]);
  const diffs: LeafDiff[] = [];
  paths.forEach(p => {
    if (!leafValuesEqual(a[p], b[p])) diffs.push({ path: p, before: a[p], after: b[p] });
  });
  diffs.sort((x, y) => x.path.localeCompare(y.path));
  return diffs;
}

/**
 * Join a correction's leaf path with a path relative to it, in bracket form.
 * An index segment concatenates ("[0]" + "[1]" -> "[0][1]"); a named segment is
 * dot-joined, unless the parent path is empty.
 */
function joinLeafPath(parent: string, child: string): string {
  if (!child) return parent;
  if (!parent) return child;
  return child.startsWith('[') ? `${parent}${child}` : `${parent}.${child}`;
}

/** True for a value that holds nested leaves rather than being one itself. */
function isContainerValue(v: unknown): boolean {
  return v !== null && typeof v === 'object';
}

/**
 * Expand container-valued corrections into one correction per changed leaf.
 *
 * {@link LeafCorrectionRows} renders `corrected_value` as a single scalar, which holds
 * for AI-audit corrections — they always name a leaf path. Human-audit corrections are
 * recorded at CONTAINER granularity (`field: "drug[0]"` carrying an object), so the row
 * stringified the object and rendered raw JSON (LA-6912). Expanding here fixes the
 * display for corrections already written, without changing what the writer records.
 *
 * A correction whose values are both scalars is returned unchanged. A container whose
 * sides differ by no leaf is also kept as-is, so a correction never silently vanishes.
 */
export function expandLeafCorrections(corrections: LeafCorrection[]): LeafCorrection[] {
  return corrections.flatMap(c => expandOneCorrection(c));
}

/** Empty for action-derivation purposes: no value was carried on this side. */
function isBlankLeaf(v: unknown): boolean {
  return v === undefined || v === null || v === '';
}

/** Per-leaf action: a CHANGE on a container can ADD one leaf and REMOVE another. */
function deriveLeafAction(before: unknown, after: unknown): string {
  if (isBlankLeaf(before) && !isBlankLeaf(after)) return 'ADD';
  if (!isBlankLeaf(before) && isBlankLeaf(after)) return 'REMOVE';
  return 'CHANGE';
}

/** One correction -> the rows it should render as. */
function expandOneCorrection(c: LeafCorrection): LeafCorrection[] {
  if (!isContainerValue(c.original_value) && !isContainerValue(c.corrected_value)) return [c];
  const diffs = computeLeafDiffs(c.original_value, c.corrected_value);
  // A container whose sides differ by no leaf is kept as-is, so a correction never
  // silently vanishes from the panel.
  if (diffs.length === 0) return [c];
  return diffs.map(d => ({
    path: joinLeafPath(c.path, d.path),
    action: deriveLeafAction(d.before, d.after),
    original_value: d.before,
    corrected_value: d.after,
    reasoning: c.reasoning,
  }));
}

/**
 * How many FIELDS one root-level correction actually changed.
 *
 * A correction on a container field (`field: "drug"`, action CHANGE) carries the whole
 * before/after structure, so the number of changed fields it represents is the number of
 * leaves that differ — not 1. Scalars (and a structure with no visible difference) count
 * as the single field the correction names, so a count is never zero for a correction that
 * claims a change.
 */
export function countChangedLeaves(original: unknown, corrected: unknown): number {
  return Math.max(computeLeafDiffs(original, corrected).length, 1);
}

/** Where an audit's "before" values can be read from, best source first. */
export interface AuditBeforeSources {
  /**
   * The audit's own frozen copy of the case output, as its auditor read it.
   * Authoritative when present.
   */
  snapshot?: Record<string, unknown>;
  /** Human audits infer differently from AI audits when there is no snapshot. */
  isHumanAudit?: boolean;
  /** This field's correction on the selected audit, if it has one. */
  correction?: Pick<CorrectionLike, 'original_value'>;
  /** Output of the last call before the audit — the AI-audit inference. */
  preAuditCallOutput?: Record<string, unknown>;
  /** The case's current output — the last resort. */
  currentOutput?: Record<string, unknown>;
}

/**
 * The value a root output field held before the audit — the "before" side of every
 * diff row.
 *
 * Prefers the audit's own `output_snapshot`, which records the output exactly as that
 * auditor read it. Everything below it infers the before-value from the case's CURRENT
 * state, which is right only until something touches the case afterwards: a later call
 * merges in, someone edits a field, another audit runs — and then the before-column
 * shows a value the auditor never saw, with nothing about it looking broken. Those
 * paths remain only for audit rows written before the snapshot existed.
 */
/**
 * The "before" value for ONE nested-leaf diff row, under a root field whose own
 * pre-audit value is `rootValue` (what `resolveAuditBeforeValue` returned for it).
 *
 * `snapshotBacked` says whether that root value came from the audit's `output_snapshot`.
 * When it did, the leaf is read from it alone: the snapshot is the single baseline, and a
 * correction's `original_value` records a different moment (the recording writer's read,
 * or a human auditor's first edit of the field), so consulting both would put two
 * different "befores" on one screen. A leaf the snapshot doesn't have reads as absent —
 * the snapshot is the whole output, so "not in it" means the auditor didn't see it.
 *
 * Without a snapshot, the legacy fallback stands: `original_value` when the correction
 * carries one, else the leaf path resolved against whatever pre-audit value the page
 * could infer. AI-audit corrections carry no `original_value` at all, which is why that
 * second half exists.
 *
 * An ADD has nothing before it either way.
 */
export function resolveLeafBeforeValue(args: {
  action?: string;
  original_value?: unknown;
  /** Leaf path relative to the root field, bracket form: "[0].coverage.copay". */
  path: string;
  rootValue: unknown;
  snapshotBacked?: boolean;
}): unknown {
  const { action, original_value, path, rootValue, snapshotBacked } = args;
  if ((action ?? '').toUpperCase() === 'ADD') return undefined;
  const fromRoot = getValueAtPath(rootValue, path);
  if (snapshotBacked) return fromRoot;
  return original_value ?? fromRoot;
}

export function resolveAuditBeforeValue(fieldPath: string, sources: AuditBeforeSources): unknown {
  const { snapshot, isHumanAudit, correction, preAuditCallOutput, currentOutput } = sources;
  if (snapshot) return snapshot[fieldPath];

  // Human audit (BI-1246): the correction's original_value was captured at the
  // auditor's first edit of the field, so it reflects exactly what they changed.
  // A field they never edited has no correction and shows the current value.
  if (isHumanAudit) {
    return correction ? correction.original_value : currentOutput?.[fieldPath];
  }
  return (preAuditCallOutput ?? currentOutput)?.[fieldPath];
}
